import { apiPath } from "./baseUrl";

const REQUEST_TIMEOUT_MS = 30000;
const UPLOAD_TIMEOUT_MS = 120000;
// La duplication serveur copie aussi les médias : délai plus long.
const DUPLICATE_TIMEOUT_MS = 300000;

export class RoadmakerApiError extends Error {
  constructor(status, data) {
    super(data?.message || "La requête a échoué.");
    this.name = "RoadmakerApiError";
    this.status = status;
    this.data = data;
  }
}

export const requestJson = async (
  path,
  {
    method = "GET",
    json,
    signal,
    timeoutMs = REQUEST_TIMEOUT_MS,
    ...options
  } = {}
) => {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0 || timeoutMs > 2147483647) {
    throw new RangeError("Délai de requête invalide.");
  }
  const controller = new AbortController();
  const requestOptions = {
    credentials: "include",
    method,
    ...(json === undefined
      ? {}
      : {
          headers: { "content-type": "application/json" },
          body: JSON.stringify(json),
        }),
    ...options,
    signal: controller.signal,
  };
  const url = apiPath(path);
  let response;
  let timer;
  let cancel;
  const deadline = new Promise((resolve, reject) => {
    cancel = () => {
      const error = signal.reason || new DOMException("Requête annulée.", "AbortError");
      reject(error);
      controller.abort(error);
    };
    if (signal?.aborted) {
      cancel();
      return;
    }
    signal?.addEventListener("abort", cancel, { once: true });
    timer = setTimeout(() => {
      // Abandonner le transport ne prouve pas l'absence d'écriture serveur.
      const error = new RoadmakerApiError(response?.status || 0, {
        code: "REQUEST_TIMEOUT",
        phase: response ? "response-body" : "request",
        message: "Délai de réponse dépassé ; le résultat de la requête n'est pas confirmé.",
      });
      reject(error);
      controller.abort(error);
    }, timeoutMs);
  });
  const readResponse = async () => {
    response = await fetch(url, requestOptions);
    let data = null;
    if (response.status !== 204) {
      try {
        data = await response.json();
      } catch (error) {
        if (controller.signal.aborted) throw error;
        throw new RoadmakerApiError(response.status, {
          message:
            "Réponse illisible : le serveur a répondu mais son corps est invalide.",
          decodeError: String((error && error.message) || error),
        });
      }
    }
    if (!response.ok) throw new RoadmakerApiError(response.status, data);
    return data;
  };
  try {
    // Le même délai couvre les en-têtes ET la lecture complète du corps.
    return await Promise.race([deadline, readResponse()]);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", cancel);
  }
};

const withLegacyDatabaseId = (entity) => {
  if (!entity || typeof entity !== "object") return entity;
  if (entity._id || entity.id === undefined) return entity;
  return { ...entity, _id: entity.id };
};

const withLegacyPoiDatabaseId = (poi) =>
  !poi || typeof poi !== "object" || poi._id || !poi.serverId
    ? poi
    : { ...poi, _id: poi.serverId };

const PROJECT_WRITE_FIELDS = [
  "name",
  "projectType",
  "tag",
  "colorProject",
  "polyline_result",
  "trkseg",
  // Traces GPX importées (2026-09-22) : complémentaires + flag du tracé
  // principal importé en remplacement (bloque le calcul Google au
  // rechargement). Sans ces clés, pickDefined les retirait du PUT.
  "importedTracks",
  "importedMainTrack",
  "customNavigationPoints",
  "customIndicationsEdited",
  "originPOI",
  "destinationPOI",
  "destinationSameAsOrigin",
  "kmlFile",
  "urlKmlToFetsh",
  "published",
];

const USER_WRITE_FIELDS = [
  "name",
  "email",
  "avatar",
  "iconIndexArray",
  "admin",
  "confirmed",
  "pack",
];

const POI_WRITE_FIELDS = [
  "id",
  "position",
  "type",
  "title",
  "subType",
  "icon",
  "description",
  "markerToDownload",
  "stopover",
  "resourceArray",
  "mainResource",
  // Lot « image principale » (2026-10-01) : checksum SHA-256 hex minuscule
  // de l'image désignée, ou "" pour effacer. Sans cette entrée, le PUT/POST
  // du client ne l'enverrait jamais (pickDefined le retirerait).
  "mainImage",
  "checkAcces",
  "draggable",
  "triggerType",
  "triggerDistance",
  "url",
  "openQuestionArray",
  "qcmArray",
  // Images de QCM image : références `poimedia:qcm:<sha256>` (ADR 0005).
  "qcmImageArray",
];

const POI_MEDIA_TYPES = ["image", "video", "audio", "icon", "qcm"];

// Enrichit le DTO média du backend avec l'URL de lecture binaire. En
// développement l'URL est relative au proxy CRA ; en production elle est
// préfixée par l'origine du backend (apiPath). Le cookie de session voyage
// avec la requête car elle reste same-origin côté client.
const withMediaSourceUrl = (projectId, poiId, media) => {
  if (!media || typeof media !== "object") return media;
  return {
    ...media,
    url: apiPath(
      `/project/${encodeURIComponent(projectId)}/pois/${encodeURIComponent(
        poiId
      )}/media/${encodeURIComponent(media.mediaType)}/${encodeURIComponent(
        media.fileName
      )}`
    ),
  };
};

const withPoiMediaUrls = (projectId, poi) => {
  const normalizedPoi = withLegacyPoiDatabaseId(poi);
  if (!normalizedPoi?.serverId || !projectId) {
    return normalizedPoi;
  }
  return {
    ...normalizedPoi,
    media: (normalizedPoi.media || []).map((media) =>
      withMediaSourceUrl(projectId, normalizedPoi.serverId, media)
    ),
  };
};

const FILE_UPLOAD_ROUTES = {
  gpx: { server: "uploadGPXOnServer", tablet: "uploadGPX" },
  txt: { server: "uploadTxtOnServer", tablet: "uploadTxt" },
  icon: { server: "uploadIconOnServer", tablet: "uploadIcon" },
  image: { server: "uploadImageOnServer", tablet: "uploadImage" },
  video: { server: "uploadVideoOnServer", tablet: "uploadVideo" },
  audio: { server: "uploadAudioOnServer", tablet: "uploadAudio" },
  imageQuiz: {
    server: "uploadImageQuizOnServer",
    tablet: "uploadImageQuiz",
  },
};

const pickDefined = (source, fields) =>
  Object.fromEntries(
    fields
      .filter((field) => source?.[field] !== undefined)
      .map((field) => [field, source[field]])
  );

const requiredSegment = (value, label) => {
  if (typeof value !== "string" || value.trim() === "") {
    throw new Error(`${label} requis.`);
  }
  return encodeURIComponent(value);
};

export const createRoadmakerApi = ({ request = requestJson } = {}) => ({
  projects: {
    list: async () => {
      const payload = await request("/project");
      return payload.projects.map(withLegacyDatabaseId);
    },
    listByUser: async (userId) => {
      const payload = await request(
        `/project/user/${requiredSegment(userId, "Identifiant utilisateur")}`
      );
      return payload.projects.map(withLegacyDatabaseId);
    },
    get: async (projectId) => {
      const payload = await request(
        `/project/${requiredSegment(projectId, "Identifiant projet")}`
      );
      return withLegacyDatabaseId(payload.project);
    },
    create: async (project) => {
      const payload = await request("/project", {
        method: "POST",
        json: pickDefined(project, PROJECT_WRITE_FIELDS),
      });
      return withLegacyDatabaseId(payload.project);
    },
    duplicate: async (projectId, name) => {
      const payload = await request(
        `/project/${requiredSegment(projectId, "Identifiant projet")}/duplicate`,
        {
          method: "POST",
          json: name === undefined ? {} : { name },
          timeoutMs: DUPLICATE_TIMEOUT_MS,
        }
      );
      return withLegacyDatabaseId(payload.project);
    },
    update: async (projectId, changes) => {
      const payload = await request(
        `/project/${requiredSegment(projectId, "Identifiant projet")}`,
        {
          method: "PUT",
          json: pickDefined(changes, PROJECT_WRITE_FIELDS),
        }
      );
      return withLegacyDatabaseId(payload.project);
    },
    remove: (projectId) =>
      request(`/project/${requiredSegment(projectId, "Identifiant projet")}`, {
        method: "DELETE",
      }),
  },
  users: {
    list: async () => {
      const payload = await request("/users");
      return payload.users.map(withLegacyDatabaseId);
    },
    get: async (userId) => {
      const payload = await request(
        `/user/${requiredSegment(userId, "Identifiant utilisateur")}`
      );
      return withLegacyDatabaseId(payload.user);
    },
    update: async (userId, changes) => {
      const payload = await request(
        `/user/${requiredSegment(userId, "Identifiant utilisateur")}`,
        {
          method: "PUT",
          json: pickDefined(changes, USER_WRITE_FIELDS),
        }
      );
      return withLegacyDatabaseId(payload.user);
    },
    remove: (userId) =>
      request(`/user/${requiredSegment(userId, "Identifiant utilisateur")}`, {
        method: "DELETE",
      }),
  },
  pois: {
    listByProject: async (projectId) => {
      const payload = await request(
        `/poi/project/${requiredSegment(projectId, "Identifiant projet")}`
      );
      return payload.pois.map((poi) => withPoiMediaUrls(projectId, poi));
    },
    get: async (poiId) => {
      const payload = await request(
        `/poi/${requiredSegment(poiId, "Identifiant POI")}`
      );
      return withPoiMediaUrls(payload.poi?.Project_id, payload.poi);
    },
    create: async (projectId, poi) => {
      const payload = await request(
        `/poi/${requiredSegment(projectId, "Identifiant projet")}`,
        {
          method: "POST",
          json: pickDefined(poi, POI_WRITE_FIELDS),
        }
      );
      return withLegacyPoiDatabaseId(payload.poi);
    },
    update: async (poiId, changes) => {
      const payload = await request(
        `/poi/${requiredSegment(poiId, "Identifiant POI")}`,
        {
          method: "PUT",
          json: pickDefined(changes, POI_WRITE_FIELDS),
        }
      );
      return withLegacyPoiDatabaseId(payload.poi);
    },
    remove: (poiId) =>
      request(`/poi/${requiredSegment(poiId, "Identifiant POI")}`, {
        method: "DELETE",
      }),
    removeByProject: (projectId) =>
      request(
        `/poi/project/${requiredSegment(projectId, "Identifiant projet")}`,
        { method: "DELETE" }
      ),
  },
  files: {
    list: (projectId) =>
      request(
        `/project/${requiredSegment(projectId, "Identifiant projet")}/files`
      ),
    upload: (projectId, fileType, file, { toTablet = false } = {}) => {
      const routes = FILE_UPLOAD_ROUTES[fileType];
      if (!routes) throw new Error("Type de fichier inconnu.");
      const formData = new FormData();
      formData.append("file", file);
      return request(
        `/project/${requiredSegment(projectId, "Identifiant projet")}/${
          toTablet ? routes.tablet : routes.server
        }`,
        { method: "POST", body: formData, timeoutMs: UPLOAD_TIMEOUT_MS }
      );
    },
    remove: (projectId, fileName) =>
      request(
        `/project/${requiredSegment(projectId, "Identifiant projet")}/files/${requiredSegment(
          fileName,
          "Nom du fichier"
        )}`,
        { method: "DELETE" }
      ),
  },
  // Partages de parcours (ADR 0006) : liens révocables sans compte.
  // create retourne le jeton brut UNE SEULE FOIS (jamais re-listable) ;
  // le lien public de lecture est reconstruit localement pour l'affichage.
  shares: {
    list: async (projectId) => {
      const payload = await request(
        `/project/${requiredSegment(projectId, "Identifiant projet")}/shares`
      );
      return payload.shares || [];
    },
    create: async (projectId, { label = "", expiresAt = null } = {}) => {
      const payload = await request(
        `/project/${requiredSegment(projectId, "Identifiant projet")}/shares`,
        {
          method: "POST",
          json: Object.assign(
            { label },
            expiresAt ? { expiresAt } : {}
          ),
        }
      );
      return payload; // { share, token }
    },
    revoke: (projectId, shareId) =>
      request(
        `/project/${requiredSegment(projectId, "Identifiant projet")}/shares/${requiredSegment(
          shareId,
          "Identifiant partage"
        )}`,
        { method: "DELETE" }
      ),
    // Lecture publique (page /share/:token) : aucune session requise ;
    // un 404 signifie jeton inconnu, révoqué ou expiré.
    readProject: async (token) => {
      const payload = await request(
        `/share/${requiredSegment(token, "Jeton de partage")}/project`
      );
      return payload.project;
    },
  },
  // Médias POI (ADR 0005) : upload binaire, listing JSON enrichi de l'URL de
  // lecture authentifiée, suppression ciblée par identifiant public
  // (mediaType, fileName). mediaType est la liste blanche fermée du backend ;
  // aucune clé de stockage ni fournisseur ne transite par le client.
  poiMedia: {
    upload: (projectId, poiId, mediaType, file) => {
      if (!POI_MEDIA_TYPES.includes(mediaType)) {
        throw new Error("Type de média inconnu.");
      }
      const formData = new FormData();
      formData.append("file", file);
      return request(
        `/project/${requiredSegment(
          projectId,
          "Identifiant projet"
        )}/pois/${requiredSegment(poiId, "Identifiant POI")}/media/${mediaType}`,
        { method: "POST", body: formData, timeoutMs: UPLOAD_TIMEOUT_MS }
      ).then((payload) => withMediaSourceUrl(projectId, poiId, payload.media));
    },
    list: async (projectId, poiId) => {
      const payload = await request(
        `/project/${requiredSegment(
          projectId,
          "Identifiant projet"
        )}/pois/${requiredSegment(poiId, "Identifiant POI")}/media`
      );
      return (payload.media || []).map((media) =>
        withMediaSourceUrl(projectId, poiId, media)
      );
    },
    remove: (projectId, poiId, media) =>
      request(
        `/project/${requiredSegment(
          projectId,
          "Identifiant projet"
        )}/pois/${requiredSegment(poiId, "Identifiant POI")}/media/${requiredSegment(
          media?.mediaType,
          "Type de média"
        )}/${requiredSegment(media?.fileName, "Nom du fichier")}`,
        { method: "DELETE" }
      ),
  },
});

export const roadmakerApi = createRoadmakerApi();

export default roadmakerApi;
